import * as React from "react";
import { propagate, gstime, eciToEcf, ecfToLookAngles, degreesToRadians, type SatRec } from "satellite.js";

/* ------------------------------------------------------------------ *
 * When is the satellite in view of the ground station? Predicts passes
 * from the group's TLE and runs the simulated mission clock (UTC): it
 * fast-forwards through the time between passes -- never more than about
 * a minute of real waiting -- and runs in real time while the satellite
 * is in view, as in real operations.
 * ------------------------------------------------------------------ */

export type GroundStation = { name: string; latDeg: number; lonDeg: number; altM: number };

export type Pass = { aos: Date; los: Date; maxElevationDeg: number; maxAt: Date };

// Below this elevation the antenna can't hold a link (terrain, buildings,
// noise near the horizon).
export const MIN_ELEVATION_DEG = 5;

const SEARCH_STEP_MS = 20_000;
const SEARCH_HORIZON_MS = 72 * 3600_000;

// Real seconds of waiting for the next pass, at most.
export const MAX_WAIT_S = 50;

export type LookAngles = { azimuthDeg: number; elevationDeg: number; rangeKm: number };

// Where the station's antenna has to point to see the satellite: compass
// direction (0° = north, 90° = east), height above the horizon, and the
// distance. Null when the orbit can't be computed for that moment.
export function lookAngles(satrec: SatRec, station: GroundStation, at: Date): LookAngles | null {
  const state = propagate(satrec, at);
  if (!state || typeof state.position !== "object") return null;
  const positionEcf = eciToEcf(state.position, gstime(at));
  const look = ecfToLookAngles(
    { latitude: degreesToRadians(station.latDeg), longitude: degreesToRadians(station.lonDeg), height: station.altM / 1000 },
    positionEcf
  );
  return {
    azimuthDeg: (((look.azimuth * 180) / Math.PI) % 360 + 360) % 360,
    elevationDeg: (look.elevation * 180) / Math.PI,
    rangeKm: look.rangeSat,
  };
}

export function elevationDeg(satrec: SatRec, station: GroundStation, at: Date): number | null {
  return lookAngles(satrec, station, at)?.elevationDeg ?? null;
}

// Narrows down the moment the elevation crosses MIN_ELEVATION_DEG between
// `before` (not in view) and `after` (in view), or the other way round.
function refineCrossing(satrec: SatRec, station: GroundStation, before: number, after: number, rising: boolean): number {
  let lo = before;
  let hi = after;
  while (hi - lo > 500) {
    const mid = (lo + hi) / 2;
    const inView = (elevationDeg(satrec, station, new Date(mid)) ?? -90) >= MIN_ELEVATION_DEG;
    if (inView === rising) hi = mid;
    else lo = mid;
  }
  return hi;
}

// The next pass starting at or after `from` (or already under way at
// `from`), within the next three days. Null if there is none, e.g. for an
// orbit never seen from the station.
export function findNextPass(satrec: SatRec, station: GroundStation, from: Date): Pass | null {
  const start = from.getTime();
  const end = start + SEARCH_HORIZON_MS;
  const inView = (t: number) => (elevationDeg(satrec, station, new Date(t)) ?? -90) >= MIN_ELEVATION_DEG;

  let aos: number | null = inView(start) ? start : null;
  let t = start;
  while (aos === null && t < end) {
    const next = t + SEARCH_STEP_MS;
    if (inView(next)) aos = refineCrossing(satrec, station, t, next, true);
    t = next;
  }
  if (aos === null) return null;

  let maxElevation = -90;
  let maxAt = aos;
  t = aos;
  while (t < aos + 24 * 3600_000) {
    const next = t + SEARCH_STEP_MS;
    const elevation = elevationDeg(satrec, station, new Date(next)) ?? -90;
    if (elevation > maxElevation) {
      maxElevation = elevation;
      maxAt = next;
    }
    if (elevation < MIN_ELEVATION_DEG) {
      const los = refineCrossing(satrec, station, t, next, false);
      return { aos: new Date(aos), los: new Date(los), maxElevationDeg: maxElevation, maxAt: new Date(maxAt) };
    }
    t = next;
  }
  // Never sets again (e.g. a geostationary satellite in view): one long pass.
  return { aos: new Date(aos), los: new Date(aos + 24 * 3600_000), maxElevationDeg: maxElevation, maxAt: new Date(maxAt) };
}

type Clock = { realAnchor: number; simAnchor: number; speed: number };

export type PassWindow = {
  // The simulated mission clock (UTC).
  simNow: Date;
  pass: Pass | null;
  inContact: boolean;
  // True while the clock fast-forwards to the next pass.
  waiting: boolean;
  // Real seconds left until the satellite rises (0 when not waiting).
  realSecondsToAos: number;
  look: LookAngles | null;
  speed: number;
  // True when no pass was found within three days.
  noPass: boolean;
  // Ends the current pass early and fast-forwards to the next one.
  skipToNextPass: () => void;
};

// Fast-forwarding speed so the wait from `simFrom` to `aos` takes at most
// MAX_WAIT_S real seconds.
const waitSpeed = (simFrom: number, aos: number) => Math.max(1, (aos - simFrom) / 1000 / MAX_WAIT_S);

export function usePassWindow(satrec: SatRec | null, station: GroundStation | null): PassWindow {
  const [clock, setClock] = React.useState<Clock | null>(null);
  const [pass, setPass] = React.useState<Pass | null>(null);
  const [noPass, setNoPass] = React.useState(false);
  const [realNow, setRealNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const timer = window.setInterval(() => setRealNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  const planFrom = React.useCallback(
    (simFrom: number) => {
      if (!satrec || !station) return;
      const next = findNextPass(satrec, station, new Date(simFrom));
      setPass(next);
      setNoPass(next === null);
      setClock({ realAnchor: Date.now(), simAnchor: simFrom, speed: next ? waitSpeed(simFrom, next.aos.getTime()) : 1 });
    },
    [satrec, station]
  );

  React.useEffect(() => {
    planFrom(Date.now());
  }, [planFrom]);

  const aos = pass?.aos.getTime() ?? null;
  const los = pass?.los.getTime() ?? null;
  const waiting = clock !== null && aos !== null && clock.simAnchor < aos;

  let simNow = clock ? clock.simAnchor + (realNow - clock.realAnchor) * clock.speed : realNow;
  // Fast-forwarding moves in big jumps; never skip past the start of contact.
  if (waiting && aos !== null) simNow = Math.min(simNow, aos);

  React.useEffect(() => {
    if (!clock || aos === null || los === null) return;
    if (waiting && simNow >= aos) {
      // In view: real time, like in real operations.
      setClock({ realAnchor: Date.now(), simAnchor: aos, speed: 1 });
    } else if (!waiting && simNow >= los) {
      planFrom(los + 1000);
    }
  }, [clock, aos, los, waiting, simNow, planFrom]);

  const inContact = !waiting && aos !== null && los !== null && simNow >= aos && simNow < los;
  const speed = clock?.speed ?? 1;

  const skipToNextPass = React.useCallback(() => {
    if (los !== null) planFrom(los + 1000);
  }, [los, planFrom]);

  return {
    simNow: new Date(simNow),
    pass,
    inContact,
    waiting,
    realSecondsToAos: waiting && aos !== null ? Math.max(0, (aos - simNow) / 1000 / speed) : 0,
    look: satrec && station ? lookAngles(satrec, station, new Date(simNow)) : null,
    speed,
    noPass,
    skipToNextPass,
  };
}

export type LinkLock = { carrier: boolean; bitSync: boolean; frameSync: boolean; commandLink: boolean };

// After the satellite rises, the station locks onto it step by step: radio
// carrier, then bit and frame synchronisation of the telemetry, and only
// then is the command link ready. A few seconds each, in real time.
export function linkLock(pw: PassWindow): LinkLock {
  if (!pw.inContact || !pw.pass) return { carrier: false, bitSync: false, frameSync: false, commandLink: false };
  const s = (pw.simNow.getTime() - pw.pass.aos.getTime()) / 1000;
  return { carrier: s >= 2, bitSync: s >= 3.5, frameSync: s >= 5, commandLink: s >= 6.5 };
}
